import {
    Card,
    CardActionArea,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import posthog from 'posthog-js';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';
import { NetCode } from '@/graphql/generated/types.generated';
import type { ConnectSurface } from '@/utils/profiles/update-profile';

/**
 * Instagram is reachable two ways and the user cannot be expected to know which one is theirs.
 *
 * The question asks which login they use, because that is what actually gates the choice: an
 * agency holding only Facebook access to a client account has no Instagram password to offer. The
 * line under each answer states how many accounts that route connects, which is what settles it
 * for the many people who hold both sets of keys and would otherwise pick the slower path.
 *
 * Deliberately absent: the words "Instagram Business". That is Meta's name for an account setting,
 * and explaining it here would put back the vocabulary this dialog exists to remove.
 */
type Route = {
    netCode: NetCode;
    icon: string;
    labelKey: string;
    descriptionKey: string;
    /** A precondition the user has to meet, shown smaller under the consequence. */
    noteKey?: string;
};

const ROUTES: Route[] = [
    {
        netCode: NetCode.InstagramV2,
        // The icon shows the login the user is about to see, which is the whole point of the
        // question. It is NOT derived from netCode: the Facebook route's code is INSTAGRAM, so
        // deriving it puts an Instagram glyph on the option that opens a Facebook login.
        icon: 'instagram',
        labelKey: 'instagram.route.direct.label',
        descriptionKey: 'instagram.route.direct.description',
    },
    {
        netCode: NetCode.Instagram,
        icon: 'facebook',
        labelKey: 'instagram.route.facebook.label',
        descriptionKey: 'instagram.route.facebook.description',
        // Without this the user picks Facebook, authorises, and gets an empty list back.
        noteKey: 'instagram.route.facebook.note',
    },
];

interface InstagramRouteDialogProps {
    open: boolean;
    /** Where the picker was opened from, e.g. 'onboarding'. Carried onto the analytics events. */
    surface?: ConnectSurface;
    onClose: () => void;
    onSelect: (netCode: NetCode) => void;
}

const InstagramRouteDialog = ({ open, surface, onClose, onSelect }: InstagramRouteDialogProps) => {
    const { t } = useTranslation();

    // The denominator for this dialog: a route-shown with no profile.connect.started after it is
    // someone who opened the question and backed out. Without it the friction this step adds is
    // unmeasurable, which is the same blind spot profile.add has.
    useEffect(() => {
        if (open && POSTHOG_ENABLED) posthog.capture('profile.connect.route-shown', { surface });
    }, [open, surface]);

    // No event here on purpose: profile.connect.started fires immediately after with the same
    // netCode and surface, so a second one would only duplicate it.
    const handleSelect = (netCode: NetCode) => {
        onSelect(netCode);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-id="instagram-route-dialog">
            <DialogTitle>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography variant="h6">{t('instagram.route.title')}</Typography>
                    <IconButton edge="end" color="inherit" onClick={onClose} aria-label={t('str.close')}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack gap={1.5} pb={1}>
                    {ROUTES.map((route) => (
                        <Card
                            key={route.netCode}
                            variant="outlined"
                            sx={{ borderRadius: 2, borderColor: 'divider' }}
                        >
                            <CardActionArea
                                onClick={() => handleSelect(route.netCode)}
                                sx={{ p: 2 }}
                                data-id={`instagram-route-${route.icon}`}
                            >
                                <Stack direction="row" alignItems="flex-start" gap={2}>
                                    <img
                                        src={`/social/${route.icon}.svg`}
                                        alt=""
                                        width={32}
                                        height={32}
                                        style={{ flexShrink: 0 }}
                                    />
                                    <Stack gap={0.25}>
                                        <Typography variant="subtitle2" color="textPrimary">
                                            {t(route.labelKey)}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {t(route.descriptionKey)}
                                        </Typography>
                                        {route.noteKey && (
                                            <Typography variant="caption" color="text.disabled">
                                                {t(route.noteKey)}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            </CardActionArea>
                        </Card>
                    ))}
                    <Typography variant="caption" color="textSecondary" textAlign="center">
                        {t('instagram.route.hint')}
                    </Typography>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default InstagramRouteDialog;
